// src/utils/frescura.js
// Antigüedad relativa de las ofertas (capa 3 del criterio unificado).
//
// El umbral fijo de 7 días no servía: cuando el extractor no corre por un mes,
// marca el 100% de las cards y una advertencia universal deja de advertir.
// Acá se distinguen dos cosas distintas:
//
//   1. El catálogo entero está viejo  → un aviso global, una sola vez.
//   2. Una oferta quedó rezagada      → un aviso en esa card, comparándola
//                                        contra la última corrida del extractor.

const DIA = 24 * 60 * 60 * 1000;

// Días que una oferta puede quedar detrás de la última corrida sin sospecha.
const REZAGO_DIAS = 2;

// Días desde la última corrida a partir de los cuales el catálogo se considera
// desactualizado en su conjunto.
const CATALOGO_VIEJO_DIAS = 7;

const aTimestamp = (valor) => {
  if (!valor) return NaN;
  const t = new Date(valor).getTime();
  return Number.isNaN(t) ? NaN : t;
};

export const diasDesde = (valor) => {
  const t = aTimestamp(valor);
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / DIA);
};

export const textoDiasDesde = (valor) => {
  const d = diasDesde(valor);
  if (d === null) return null;
  if (d <= 0) return 'hoy';
  if (d === 1) return 'hace 1 día';
  return `hace ${d} días`;
};

/**
 * Lo mismo, pero para empezar una frase: 'Hoy', 'Hace 1 día', 'Hace 3 días'.
 *
 * `textoDiasDesde` va en minúscula porque se usa dentro de una oración ("las
 * ofertas se actualizaron hace 3 días"). El distintivo de las cards no tiene
 * oración alrededor: es la frase entera, y en minúscula se lee como un recorte.
 */
export const textoAntiguedad = (valor) => {
  const t = textoDiasDesde(valor);
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : null;
};

/**
 * Construye el evaluador de frescura para un conjunto de ofertas.
 *
 * @param {Array} items      ofertas cargadas
 * @param {Function} getFecha cómo obtener la fecha de actualización de cada una
 * @returns {{ultimaCorrida:number|null, diasCatalogo:number|null,
 *            catalogoDesactualizado:boolean, esRezagada:Function}}
 */
export const construirFrescura = (items, getFecha = (i) => i?.last_update) => {
  const timestamps = (items || [])
    .map(i => aTimestamp(getFecha(i)))
    .filter(t => !Number.isNaN(t));

  if (timestamps.length === 0) {
    return {
      ultimaCorrida: null,
      diasCatalogo: null,
      catalogoDesactualizado: false,
      esRezagada: () => false,
    };
  }

  const ultimaCorrida = Math.max(...timestamps);
  const diasCatalogo  = Math.floor((Date.now() - ultimaCorrida) / DIA);

  return {
    ultimaCorrida,
    diasCatalogo,
    catalogoDesactualizado: diasCatalogo > CATALOGO_VIEJO_DIAS,
    // Rezagada respecto del resto del catálogo, no respecto de hoy.
    esRezagada: (fecha) => {
      const t = aTimestamp(fecha);
      if (Number.isNaN(t)) return false;
      return (ultimaCorrida - t) > REZAGO_DIAS * DIA;
    },
  };
};
